import { useRef } from 'react';
import { featureUtil } from 'utils/geoJSONFeatureUtils/featureUtil';
import { v4 as uuidv4 } from 'uuid';

import useMapContext from 'components/Map/hooks/useMapContext';

import {
    BUFFER_METERS,
    GEOJSON_BOUNDARY_TYPE,
    GEOJSON_PROPERTY,
    MAP_LAYER,
    OutlineMode
} from './constants';
import type { Feature, LineString } from './types';

const useFeatureOutline = () => {
    const { features } = useMapContext();
    const { addFeature, removeFeature, updateFeature } = features;

    const boundingBoxFeaturesRef = useRef<Map<string, Feature<LineString>>>(new Map());

    const {
        calculateBoundingBoxCoordinates,
        createLineStringFeature,
        getGeometryOutlineCoordinates
    } = featureUtil;

    const getOutlineCoordinates = (feature: Feature, outlineMode?: OutlineMode) => {
        if (outlineMode === OutlineMode.GEOMETRY_OUTLINE) {
            return getGeometryOutlineCoordinates(feature);
        }

        return calculateBoundingBoxCoordinates(feature, BUFFER_METERS);
    };

    const getOutlineLayer = (feature: Feature) => {
        const isNoEntry =
            feature.properties?.[GEOJSON_PROPERTY.BOUNDARY_TYPE] === GEOJSON_BOUNDARY_TYPE.NO_ENTRY;

        if (feature.properties?.[GEOJSON_PROPERTY.IS_STATIC]) {
            return isNoEntry
                ? MAP_LAYER.STATIC_OBSTACLE_BOUNDING_BOX_OUTLINE
                : MAP_LAYER.STATIC_BOUNDING_BOX_OUTLINE;
        }

        return isNoEntry
            ? MAP_LAYER.DRAWING_OBSTACLE_BOUNDING_BOX_OUTLINE
            : MAP_LAYER.DRAWING_BOUNDING_BOX_OUTLINE;
    };

    const addFeatureOutline = (feature: Feature, outlineMode?: OutlineMode) => {
        const outlineCoordinates = getOutlineCoordinates(feature, outlineMode);
        const boundingBoxId = uuidv4();
        const boundingBoxFeature = createLineStringFeature(outlineCoordinates, boundingBoxId);

        boundingBoxFeature.properties = {
            ...boundingBoxFeature.properties,
            [GEOJSON_PROPERTY.INNER_FEATURE_ID]: feature.id,
            [GEOJSON_PROPERTY.IS_STATIC]: feature.properties?.[GEOJSON_PROPERTY.IS_STATIC]
        };

        boundingBoxFeaturesRef.current.set(feature.id as string, boundingBoxFeature);

        const layerName = getOutlineLayer(feature);

        addFeature({
            feature: boundingBoxFeature,
            layerName
        });

        return boundingBoxFeature;
    };

    const upsertFeatureOutline = (feature: Feature, outlineMode?: OutlineMode) => {
        const boundingBoxFeature = boundingBoxFeaturesRef.current.get(feature.id as string);

        if (!boundingBoxFeature) {
            return addFeatureOutline(feature, outlineMode);
        }

        boundingBoxFeature.geometry.coordinates = getOutlineCoordinates(feature, outlineMode);
        updateFeature(boundingBoxFeature);

        return boundingBoxFeature;
    };

    const removeFeatureOutline = (featureId: string) => {
        const boundingBoxFeature = boundingBoxFeaturesRef.current.get(featureId);

        if (!boundingBoxFeature) {
            return;
        }

        removeFeature(boundingBoxFeature.id as string);
        boundingBoxFeaturesRef.current.delete(featureId);
    };

    return {
        removeFeatureOutline,
        upsertFeatureOutline
    };
};

export default useFeatureOutline;
